/* [캠퍼스] 목동(본관) · 검단(검단관) 구분의 기준 — 화면 쪽 사본.

   ⚠️ 서버 사본 functions/campuses.js 와 글자 하나까지 같아야 합니다.
      CRA 는 src 밖을 import 하지 못해 사본이 둘입니다. tests/campus/campus.test.mjs 가 두 사본을 직접 대조합니다.

   [원칙 — docs/campus/IMPLEMENT.md §0]
   - 값은 고정 영문 'mokdong' | 'geomdan'. 필드 이름은 campusId (campus 는 입시 모듈이 '대학 캠퍼스' 뜻으로 씀 — 금지).
   - 이력 문서(세션·문자·출결·상담…)의 campusId = '일이 일어난 곳'. users 는 '현재 소속'
     (학생 homeCampusId, 교직원 campusMokdong/campusGeomdan 불리언, 학부모는 자녀에서 파생).
   - campusId 가 없는 문서: 컷오프(settings/campuses.cutoffAt) 이전이면 목동, 이후면 '미지정'(지어내지 않음).
     컷오프를 정하기 전에는 모두 목동입니다 — 그래서 옛 기록을 한꺼번에 고치지 않고도 목동 동작이 그대로입니다.
   - 학생·학부모·공개 화면에는 systemLabel('본관'·'검단관')을 쓰지 않습니다(인천 시행규칙 제5조① — 분원 혼동 명칭).
     등록 명칭(registeredName)과 주소로 구분합니다. 두 학원의 등록 명칭이 같을 예정(원장 결정 10-29)이기 때문입니다.

   캠퍼스 값의 원본은 settings/campuses 문서(원장만 수정)이고, 이 파일은 그 문서가 없거나 읽기 전의 기본값입니다.
   비밀값(토스 키·게이트웨이 비밀번호 등)은 여기에도 settings 에도 넣지 않습니다. */

export const CAMPUS_IDS = ['mokdong', 'geomdan'];
export const DEFAULT_CAMPUS_ID = 'mokdong';

/* 문자 게이트웨이 토픽. 목동은 지금 폰 앱이 구독하는 이름을 그대로 둡니다 — 업데이트 안 된 목동 폰이 계속 받게. */
export const LEGACY_SMS_TOPIC = 'imperial_sms_gateway';

export const CAMPUS_DEFAULTS = Object.freeze({
    mokdong: Object.freeze({
        id: 'mokdong',
        systemLabel: '본관',
        region: '목동',
        registeredName: '목동임페리얼학원',
        address: '서울 양천구 등촌로 192, 4층',
        phone: '02-2644-1178',
        smsPhone: '',
        educationOffice: '서울특별시강서양천교육지원청',
        registrationNo: '',
        /* 교습 종료 시각(조례). 서울은 초·중·고 모두 22시. */
        endTimes: Object.freeze({ elem: '22:00', mid: '22:00', high: '22:00' }),
        status: 'active',
        smsTopic: LEGACY_SMS_TOPIC,
        features: Object.freeze({ attendanceSms: false, tuitionPay: false })
    }),
    geomdan: Object.freeze({
        id: 'geomdan',
        systemLabel: '검단관',
        region: '검단',
        /* 원장 결정(10-29): 검단도 '목동임페리얼학원'으로 등록 예정 — 교육지원청 확인 전 */
        registeredName: '목동임페리얼학원',
        address: '',
        phone: '',
        /* 검단 전용 법인폰(원장 11-04). 등·하원·결제 안내 문자는 이 폰에서만 나갑니다. */
        smsPhone: '010-7382-2265',
        educationOffice: '인천광역시서부교육지원청',
        registrationNo: '',
        /* 인천 조례: 초 21시, 중 22시, 고 23시 */
        endTimes: Object.freeze({ elem: '21:00', mid: '22:00', high: '23:00' }),
        /* preparing = 원장·시험 계정에만 보임. 개원일에 원장이 active 로 바꿉니다(코드 배포 없이). */
        status: 'preparing',
        smsTopic: 'imperial_sms_gateway_geomdan',
        features: Object.freeze({ attendanceSms: false, tuitionPay: false })
    })
});

/* settings/campuses 에서 원장이 바꿀 수 있는 칸. id·smsTopic 은 코드만 정합니다(토픽이 바뀌면 문자가 엉뚱한 폰으로 감). */
export const EDITABLE_CAMPUS_FIELDS = [
    'systemLabel', 'region', 'registeredName', 'address', 'phone', 'smsPhone',
    'educationOffice', 'registrationNo', 'endTimes', 'status', 'features'
];
export const CAMPUS_STATUSES = ['preparing', 'active'];

/** 알려진 캠퍼스 ID 면 그대로, 아니면 null. 대소문자·공백을 고쳐 주지 않습니다 —
    'Geomdan'·'geomdam' 같은 값이 조용히 통과해 모든 필터에서 빠지는 것을 막으려고 받는 쪽에서 거절합니다. */
export const toCampusId = (v) => (typeof v === 'string' && CAMPUS_IDS.includes(v) ? v : null);
export const isCampusId = (v) => toCampusId(v) !== null;

const plainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

/** settings/campuses 문서(없으면 undefined)를 기본값 위에 얹어 캠퍼스 목록을 만듭니다.
    알 수 없는 캠퍼스·칸·값은 버립니다(문서 오타가 화면을 깨지 않게). */
export const mergeCampusSettings = (settingsDoc) => {
    const over = plainObject(settingsDoc) && plainObject(settingsDoc.campuses) ? settingsDoc.campuses : {};
    return CAMPUS_IDS.map((id) => {
        const base = CAMPUS_DEFAULTS[id];
        const o = plainObject(over[id]) ? over[id] : {};
        const out = { ...base, endTimes: { ...base.endTimes }, features: { ...base.features } };
        EDITABLE_CAMPUS_FIELDS.forEach((k) => {
            if (!(k in o)) return;
            const v = o[k];
            if (k === 'status') { if (CAMPUS_STATUSES.includes(v)) out.status = v; return; }
            if (k === 'endTimes') {
                if (plainObject(v)) ['elem', 'mid', 'high'].forEach((g) => {
                    if (typeof v[g] === 'string' && /^\d{2}:\d{2}$/.test(v[g])) out.endTimes[g] = v[g];
                });
                return;
            }
            if (k === 'features') {
                if (plainObject(v)) Object.keys(base.features).forEach((f) => {
                    if (typeof v[f] === 'boolean') out.features[f] = v[f];
                });
                return;
            }
            if (typeof v === 'string') out[k] = v.slice(0, 200);
        });
        return out;
    });
};

/** 컷오프 시각(밀리초). settings/campuses.cutoffAt(Timestamp·Date·숫자·ISO 문자열) — 없으면 null(= 아직 모두 목동). */
export const cutoffMillis = (settingsDoc) => toMillis(plainObject(settingsDoc) ? settingsDoc.cutoffAt : null);

export function toMillis(v) {
    if (v == null) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
    return null;
}

/** 이력 문서의 캠퍼스. campusId 가 있으면 그것, 없으면 컷오프 이전(또는 컷오프 미정)이면 목동, 이후면 null(미지정).
    만든 시각을 모르는 문서는 컷오프 이전으로 봅니다 — 옛 문서일수록 시각 칸이 없습니다. */
export const campusOfDoc = (d, cutoffMs = null) => {
    if (!d) return null;
    const own = toCampusId(d.campusId);
    if (own) return own;
    if (d.campusId != null && d.campusId !== '') return null;    // 알 수 없는 값 — 지어내지 않음
    if (cutoffMs == null) return DEFAULT_CAMPUS_ID;
    const created = toMillis(d.createdAt);
    if (created == null || created < cutoffMs) return DEFAULT_CAMPUS_ID;
    return null;
};

/** 사람의 현재 소속 캠퍼스 목록.
    학생: homeCampusId 하나. 교직원: campusMokdong / campusGeomdan 이 true 인 것. 학부모: 자녀들의 소속(childrenById 필요).
    아무 표시가 없는 사람은 컷오프 규칙을 따릅니다(옛 계정 = 목동). */
export const campusesOfUser = (u, { cutoffMs = null, childrenById = null } = {}) => {
    if (!u) return [];
    const role = String(u.role || '');
    if (role === 'student') {
        const c = toCampusId(u.homeCampusId) || campusOfDoc({ campusId: u.homeCampusId, createdAt: u.createdAt }, cutoffMs);
        return c ? [c] : [];
    }
    if (role === 'parent') {
        const ids = Array.isArray(u.linkedChildrenIds) ? u.linkedChildrenIds : [];
        const found = new Set();
        if (childrenById) ids.forEach((cid) => {
            const child = typeof childrenById.get === 'function' ? childrenById.get(cid) : childrenById[cid];
            if (child) campusesOfUser(child, { cutoffMs }).forEach((c) => found.add(c));
        });
        if (found.size) return CAMPUS_IDS.filter((c) => found.has(c));
        const c = campusOfDoc({ createdAt: u.createdAt }, cutoffMs);
        return c ? [c] : [];
    }
    const flagged = CAMPUS_IDS.filter((c) => u[campusFlagKey(c)] === true);
    if (flagged.length) return flagged;
    const c = campusOfDoc({ createdAt: u.createdAt }, cutoffMs);
    return c ? [c] : [];
};

/** 교직원 소속 불리언 칸 이름: mokdong → campusMokdong */
export const campusFlagKey = (id) => `campus${id.charAt(0).toUpperCase()}${id.slice(1)}`;
export const CAMPUS_FLAG_KEYS = CAMPUS_IDS.map(campusFlagKey);

/** 문자 발송함 문서가 갈 게이트웨이 토픽. campusId 가 없거나 목동이면 지금 목동 폰 토픽.
    검단 문서는 검단 토픽 — 그 토픽을 듣는 폰이 없으면 문자는 나가지 않고 하루 뒤 '실패'가 됩니다(목동 폰으로 새지 않음). */
export const topicForSms = (d) => {
    const c = toCampusId(d && d.campusId);
    return c ? CAMPUS_DEFAULTS[c].smsTopic : LEGACY_SMS_TOPIC;
};

/** 화면에서 이 사람이 고를 수 있는 캠퍼스(전환 칩). 원장은 준비 중 캠퍼스도 봅니다. */
export const selectableCampuses = (campusList, user, opts = {}) => {
    if (!user) return [];
    const isOwner = user.role === 'admin';
    const mine = isOwner ? CAMPUS_IDS : campusesOfUser(user, opts);
    return campusList.filter((c) => mine.includes(c.id) && (c.status === 'active' || isOwner));
};
